import './post-audio-player.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { IconExplicit } from '../../../common/icons/icon-explicit';
import { IconMagnifyingGlassPlus } from '../../../common/icons/icon-magnifying-glass-plus';
import { IconPlayerPause } from '../../../common/icons/icon-player-pause';
import { IconPlayerPlay } from '../../../common/icons/icon-player-play';
import { IconWaveform } from '../../../common/icons/icon-waveform/icon-waveform';
import { useTranslation } from '../../../i18n';
import { NeedAccessButton } from '../../../need-access/components/need-access-button/need-access-button.component';
import { NeedAccessContentType } from '../../../need-access/need-access.interface';
import { ContentType } from '../../../perks/model/content-type.enum';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { buttonize } from '../../../utils/a11y.utils';
import { getTrackId, postContentToTrack } from '../../models/album.helpers';
import { getAudioEngine } from '../../services/audio-engine';
import { playPostAudio, setMusicIsPlaying } from '../../store/music.reducer';
import {
  selectMusicIsPlaying,
  selectPostQueue,
  selectQueueSource,
} from '../../store/music.selectors';
import { ArtLightbox } from '../art-lightbox/art-lightbox.component';

interface Props {
  contentId: string;
  playbackId?: string | null;
  token?: string | null;
  title?: string;
  artist?: string;
  explicit?: boolean;
  artworkUrl?: string;
  duration?: number;
  /** Locked teaser: blurred art, ghost meta, play opens the need-access modal. */
  locked?: boolean;
  /** Post id for the need-access request; omit to render the play button inert. */
  postId?: string;
}

// Times of an hour or more show H:MM:SS (1:05:30) instead of 65:30.
const formatTime = (s?: number): string => {
  if (s === undefined || !Number.isFinite(s) || s < 0) return '';
  const secs = Math.floor(s % 60)
    .toString()
    .padStart(2, '0');
  if (s < 3600) return `${Math.floor(s / 60)}:${secs}`;
  const mins = Math.floor((s % 3600) / 60)
    .toString()
    .padStart(2, '0');
  return `${Math.floor(s / 3600)}:${mins}:${secs}`;
};

/**
 * Remote control + view over the shared footer bar (SNGL-7191): play
 * dispatches a post queue into the music slice, the bar claims the engine and
 * drives playback (same dispatch-driven pattern as the tracklist rows), and
 * this component mirrors progress by listening to the engine read-only.
 * Errors and token refresh are the bar's job.
 */
export const PostAudioPlayer = ({
  contentId,
  playbackId,
  token,
  title,
  artist,
  explicit,
  artworkUrl,
  duration: contentDuration,
  locked,
  postId,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const engineRef = useRef(getAudioEngine());

  const isPlayingGlobal = useAppSelector(selectMusicIsPlaying);
  const queueSource = useAppSelector(selectQueueSource);
  const postQueue = useAppSelector(selectPostQueue);

  const isActiveQueue =
    queueSource === 'post' && !!postQueue[0] && getTrackId(postQueue[0]) === contentId;
  const isPlaying = isActiveQueue && isPlayingGlobal;

  const [engineTime, setEngineTime] = useState(0);
  const [engineDuration, setEngineDuration] = useState(0);
  const [hoverTime, setHoverTime] = useState(0);
  const [titleScrolling, setTitleScrolling] = useState(false);
  const [artistScrolling, setArtistScrolling] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const trackWrapRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const artistRef = useRef<HTMLDivElement>(null);

  // Marquee long titles/artists — same treatment as the album player bar
  useEffect(() => {
    const setupMarquee = () => {
      const els: Array<[HTMLElement | null, (v: boolean) => void]> = [
        [titleRef.current, setTitleScrolling],
        [artistRef.current, setArtistScrolling],
      ];
      const overflows = els.map(([el]) => (el ? Math.max(0, el.scrollWidth - el.offsetWidth) : 0));
      const maxOverflow = Math.max(...overflows);
      const dur = `${maxOverflow / 14 + 8}s`;
      els.forEach(([el, setScrolling], i) => {
        if (!el) return;
        if (overflows[i] > 0 && maxOverflow > 0) {
          el.style.setProperty('--scroll-distance', `-${overflows[i]}px`);
          el.style.setProperty('--scroll-duration', dur);
          setScrolling(true);
        } else {
          el.style.removeProperty('--scroll-distance');
          el.style.removeProperty('--scroll-duration');
          setScrolling(false);
        }
      });
    };
    const schedule = () => requestAnimationFrame(() => requestAnimationFrame(setupMarquee));
    schedule();
    window.addEventListener('resize', schedule);
    return () => window.removeEventListener('resize', schedule);
  }, [title, artist]);

  useEffect(() => {
    const engine = engineRef.current;
    const sync = () => {
      if (!playbackId || engine.currentPlaybackId !== playbackId) return;
      setEngineTime(engine.el.currentTime ?? 0);
      if (engine.el.duration) setEngineDuration(engine.el.duration);
    };
    const offs = [engine.on('timeupdate', sync), engine.on('loadedmetadata', sync)];
    return () => offs.forEach((off) => off());
  }, [playbackId]);

  if (locked) {
    return (
      <div class="single-post-audio-player single-post-audio-player--locked">
        {artworkUrl && (
          <div class="single-post-audio-art">
            <img src={artworkUrl} alt={t('a11y.audioArtwork')} />
          </div>
        )}
        <div class="single-post-audio-body">
          <div class="single-post-audio-titles">
            <div class="single-post-audio-ghost-bar" />
            <div class="single-post-audio-ghost-bar single-post-audio-ghost-bar--short" />
          </div>
          {postId ? (
            <NeedAccessButton
              contentType={NeedAccessContentType.POST}
              contentId={postId}
              isPlayBtn={true}
              playerIcon={true}
              showLock={false}
              message={''}
            />
          ) : (
            <button class="single-post-audio-toggle" aria-label={t('a11y.play')} disabled>
              <IconPlayerPlay width={25} height={25} />
            </button>
          )}
          <div class="single-post-audio-transport">
            <span class="single-post-audio-time">0:00</span>
            <div class="single-post-audio-track">
              <div class="single-post-audio-fill" />
            </div>
            <span class="single-post-audio-time">0:00</span>
          </div>
        </div>
      </div>
    );
  }

  if (!playbackId) return null;

  const duration = (isActiveQueue && engineDuration) || contentDuration || 0;
  const currentTime = isActiveQueue ? engineTime : 0;
  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleToggle = () => {
    if (isPlaying) {
      dispatch(setMusicIsPlaying(false));
      return;
    }
    if (isActiveQueue) {
      dispatch(setMusicIsPlaying(true));
      return;
    }
    dispatch(
      playPostAudio(
        postContentToTrack(
          {
            id: contentId,
            contentType: ContentType.AUDIO,
            title,
            artist,
            explicit,
            playbackId,
            token,
            duration: contentDuration,
          },
          artworkUrl,
          postId
        )
      )
    );
  };

  const handleSeek = (e: Event) => {
    if (!isActiveQueue) return;
    const value = Number((e.target as HTMLInputElement).value);
    if (duration > 0) {
      const time = (value / 1000) * duration;
      engineRef.current.el.currentTime = time;
      setEngineTime(time);
    }
  };

  const handleTrackMouseMove = (e: MouseEvent) => {
    const wrap = trackWrapRef.current;
    if (!wrap || duration <= 0) return;
    const rect = wrap.getBoundingClientRect();
    const hoverPct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    setHoverTime(hoverPct * duration);
    wrap.style.setProperty('--hover-pct', `${hoverPct * 100}%`);
    const tooltip = wrap.querySelector('.single-post-audio-tooltip') as HTMLElement | null;
    if (tooltip) tooltip.style.left = `${hoverPct * 100}%`;
  };

  // Touch has no hover: while a finger (or mouse) is down on the seekbar the track grows and
  // shows the thumb, which follows the drag live — see the .seeking styles (SNGL-7207). The
  // release is caught on window: a native slider drag can end off the element, so a pointerup
  // handler on the input itself leaves the class stuck on.
  const beginSeeking = () => {
    trackWrapRef.current?.classList.add('seeking');
    const end = () => {
      trackWrapRef.current?.classList.remove('seeking');
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  return (
    <div class="single-post-audio-player">
      {artworkUrl && (
        <div
          class="single-post-audio-art"
          {...buttonize(() => setLightboxOpen(true), t('a11y.viewArtwork'))}
        >
          <img
            src={artworkUrl}
            alt={title ? t('a11y.trackArtwork', { title }) : t('a11y.audioArtwork')}
          />
          {isPlaying && (
            <span class="single-post-audio-art-waveform" aria-hidden="true">
              <IconWaveform fill="#fff" animated />
            </span>
          )}
          <span class="single-post-audio-art-zoom" aria-hidden="true">
            <IconMagnifyingGlassPlus width={15} height={15} fill="#fff" />
          </span>
        </div>
      )}
      {lightboxOpen && artworkUrl && (
        <ArtLightbox
          images={[{ url: artworkUrl.replace(/\/MEDIUM$/, '/FULL'), label: title }]}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      <div class="single-post-audio-body">
        {(title || artist) && (
          <div class="single-post-audio-titles">
            {title && (
              <h4
                ref={titleRef}
                class={`single-post-audio-title h4 single-h4${titleScrolling ? ' scrolling' : ''}`}
              >
                {title}
              </h4>
            )}
            {artist && (
              <div
                ref={artistRef}
                class={`single-post-audio-artist${artistScrolling ? ' scrolling' : ''}`}
              >
                {explicit && <IconExplicit width={12} height={12} />}
                <span>{artist}</span>
              </div>
            )}
          </div>
        )}
        <button
          class="single-post-audio-toggle"
          aria-label={isPlaying ? t('a11y.pause') : t('a11y.play')}
          onClick={handleToggle}
        >
          {isPlaying ? (
            <IconPlayerPause width={25} height={25} />
          ) : (
            <IconPlayerPlay width={25} height={25} />
          )}
        </button>
        <div class="single-post-audio-transport">
          <span class="single-post-audio-time">{formatTime(currentTime) || '0:00'}</span>
          <div
            class="single-post-audio-track"
            ref={trackWrapRef}
            style={{ '--thumb-pct': `${pct}%` }}
            onMouseMove={handleTrackMouseMove}
          >
            <div class="single-post-audio-fill" />
            <div class="single-post-audio-thumb" />
            <div class="single-post-audio-tooltip">{formatTime(hoverTime) || '0:00'}</div>
            <input
              type="range"
              class="single-post-audio-seekbar"
              min={0}
              max={1000}
              value={Math.round(pct * 10)}
              aria-label={t('a11y.seek')}
              disabled={!isActiveQueue}
              onInput={handleSeek}
              onPointerDown={beginSeeking}
            />
          </div>
          <span class="single-post-audio-time">{formatTime(duration) || '0:00'}</span>
        </div>
      </div>
    </div>
  );
};
